import { Box, HStack, VStack, useDisclosure } from '@chakra-ui/react';
import { BackToTopButton } from 'Atoms/BackToTopButton';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AggregatedMetricsTableData } from '../../AggregatedMetrics';
import { useParams } from 'react-router-dom';
import { Typography } from 'Tokens';
import {
  GetMetricsDrQuery_,
  QuestionType_Enum_,
  useGetAnswersForDrMetricsOnCompanyLevelQuery,
  useGetAnswersForDrMetricsOnGroupLevelQuery,
  useGetMetricAnswersPerDrQuery,
  useGetTargetsDrQuery,
} from 'models';
import { useCurrentCompany } from 'utils/hooks';
import { ClickableReportAnswer } from './ReportClickableAnswers/ClickableReportAnswer';
import { MetricSidebar } from '../../MetricSidebar';
import { TimePeriodsEnums } from '../../../Requirement';
import { MetricsTableData, filterMetricLevels, useGetAggregatedMetrics } from '../..';
import { SelectedMetric } from 'containers/Esrs/EsrsAssessment.hooks';
import { OptOutModal } from '../../OptOutModal';
import { getDisclosureRequirementLink } from 'utils/links';
import { EsrsStructure } from '../DisclosureRequirement.utils';
import { NarrativeReportAnswer } from './ReportClickableAnswers/NarrativeReportAnswer';
import { orderBy, uniqBy } from 'lodash';
import { BottomNavigation } from 'Molecules/BottomNavigation';
import { Loader, TOP_MENU_HEIGHT } from 'Molecules';
import { filterOptedOutRows } from 'containers/Esrs/pieces/Report/Report.hooks';
import {
  E1_5Metrics,
  E1_6_Target_Metrics,
  FrameworkRefs,
  Frameworks,
  hardcodedMetrics,
  S1_6Metrics,
  S1_8Metrics,
} from 'containers/Esrs/pieces';
import { DRHardcodedTables } from './DRHardcodedTables';
import { AssessableMetrics, GeneratedSummaryStatus } from '../../Metrics';
import { processReportTableData } from 'containers/Esrs/pieces/Report/Report.helpers';
import { E1_6TargetMetrics } from '../../../../Report/DocGenerator/CustomTables/E1-6-targets.ts';
import { StackedMdrmHardcodedTables } from './StackedMdrmHardcodedTables.tsx';
import { mfStandardRefs } from 'containers/Esrs/utils.ts';

const hardcodedTableRefs = [...hardcodedMetrics, ...E1_6_Target_Metrics];

export const getHardcodedMetrics = (drRef: string): string[] => {
  switch (drRef) {
    case 'S1-8':
      return S1_8Metrics;
    case 'E1-5':
      return E1_5Metrics;
    case 'E1-6':
      return E1_6TargetMetrics.map((m) => m.reference);
    case 'S1-6':
      return S1_6Metrics;
    default:
      return [];
  }
};
export const EditableReport = ({
  isLoading,
  companyLevelReportingUnitId,
  companyAssessmentId,
  isGroup,
  metricDR,
  standards,
  metrics,
  year,
}: {
  isLoading: boolean;
  companyLevelReportingUnitId: string;
  companyAssessmentId: string;
  isGroup: boolean;
  metricDR: Partial<GetMetricsDrQuery_['DisclosureRequirement_by_pk']>;
  metrics: AssessableMetrics;
  standards: EsrsStructure['standards'];
  year: number | undefined;
}) => {
  const reportRef = useRef<HTMLDivElement>(null);
  const company = useCurrentCompany();
  const [selectedMetric, setSelectedMetric] = useState<SelectedMetric | undefined>();
  const [rowData, setRowData] = useState<MetricsTableData>();
  const [sidebarMetric, setSidebarMetric] = useState<SelectedMetric>();

  const [selectedMdrm, setSelectedMdrm] = useState<string | undefined>();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    companyId = '',
    esrsAssessmentId = '',
    standardRef = '',
    disclosureRequirementRef = '',
    framework = '',
  } = useParams();
  const [generatedSummaryStatus, setGeneratedSummaryStatus] = useState<GeneratedSummaryStatus>();
  const [showBackToTop, setShowBackToTop] = useState(false);
  const handleScroll = useCallback(() => {
    setShowBackToTop((reportRef.current?.scrollTop ?? 0) > 0);
  }, []);

  const { data: companyMetricAnswersData, loading: loadingCompany } =
    useGetAnswersForDrMetricsOnCompanyLevelQuery({
      variables: { esrsAssessmentId, disclosureRequirementRef, frameworkRef: framework },
      skip: !disclosureRequirementRef || !esrsAssessmentId,
    });
  const { data: groupMetricAnswersData, loading: loadingGroup } =
    useGetAnswersForDrMetricsOnGroupLevelQuery({
      variables: { esrsAssessmentId, disclosureRequirementRef, frameworkRef: framework },
      skip: !isGroup || !disclosureRequirementRef || !esrsAssessmentId,
    });
  const { data: targetsData } = useGetTargetsDrQuery({
    variables: {
      reference: 'E1-4',
      esrsAssessmentId,
      standardRef,
    },
    skip: !esrsAssessmentId || !standardRef || disclosureRequirementRef !== 'E1-6',
  });

  const companyAnswersData = useMemo(
    () => companyMetricAnswersData?.EsrsAssessment_by_pk?.reportingUnits ?? [],
    [companyMetricAnswersData]
  );

  const groupAnswersData = useMemo(
    () => groupMetricAnswersData?.EsrsAssessment_by_pk?.subsidiaryAssessments ?? [],
    [groupMetricAnswersData]
  );

  const { allAggregatedMetrics } = useGetAggregatedMetrics(
    metrics ?? [],
    standardRef,
    companyAssessmentId,
    companyAnswersData,
    groupAnswersData,
    isGroup
  );

  const isEsrs = useMemo(() => framework === FrameworkRefs.esrs, [framework]);

  const processedMetrics = useMemo(() => {
    const uniqueRefs: string[] = [];
    const result = allAggregatedMetrics.map((metric) => processReportTableData(metric, uniqueRefs));
    return result;
  }, [allAggregatedMetrics]);

  const isTargetOrAction = useMemo(
    () => metricDR?.type === 'action' || metricDR?.type === 'target',
    [metricDR]
  );
  const filteredMetrics = useMemo(() => {
    if (metrics) {
      const filteredLevels = isEsrs
        ? filterMetricLevels(metrics, companyAssessmentId).filter(
            (m) => !hardcodedTableRefs.includes(m.reference)
          )
        : filterMetricLevels(metrics, companyAssessmentId);
      return uniqBy(
        orderBy(
          filteredLevels,
          [
            (metric) => metric.requirement.frameworkRef !== framework,
            (metric) => metric.globalOrder === null,
            (metric) => metric.globalOrder,
            (metric) => metric.isAR,
            (metric) => metric.order,
            (metric) => metric.firstLevel,
            (metric) => metric.secondLevel,
            (metric) => metric.reference,
          ],
          ['asc', 'asc', 'asc', 'asc', 'asc', 'asc', 'asc', 'asc']
        ),
        'reference'
      );
    }
    return [];
  }, [metrics]);

  const [withAssociationMetrics, withoutAssociationMetrics] = useMemo(() => {
    return [
      filteredMetrics.filter((metric) => metric.hasAssociation === true) ?? [],
      filteredMetrics.filter((metric) => metric.hasAssociation === false) ?? [],
    ];
  }, [filteredMetrics]);

  const { data: answersData } = useGetMetricAnswersPerDrQuery({
    variables: {
      reportingUnitId: companyLevelReportingUnitId,
      drRef: disclosureRequirementRef,
      frameworkRef: framework,
    },
  });

  const drTypeName = useMemo(
    () => (metricDR?.type === 'target' ? 'targets' : 'actions'),
    [metricDR]
  );

  const answersMap = useMemo(() => {
    const map = new Map();
    answersData?.esrs_Answer.forEach((ans) => {
      map.set(ans.metricRef, ans.datapoints.find((dp) => !!dp.value)?.value);
    });
    return map;
  }, [answersData]);

  const standard = useMemo(
    () => standards.find((std) => std.reference === standardRef),
    [standards]
  );

  const currentDrIndex = useMemo(
    () =>
      standard?.disclosureRequirementRefs.findIndex(
        (req) => req.reference === metricDR?.reference
      ) ?? 0,
    [standard, metricDR]
  );

  const [standardName, showStandardName] = useMemo(() => {
    const drIndex = standard?.disclosureRequirementRefs.findIndex(
      (dr) => dr.reference === disclosureRequirementRef
    );
    return [standard?.title, drIndex === 0];
  }, [standard, standardRef]);

  const [categoryName, showCategoryName] = useMemo(() => {
    const standardIndex = standards
      .filter((std) => std.category === standard?.category)
      .findIndex((std) => std.reference === standardRef);
    return [standard?.category, standardIndex === 0 && currentDrIndex === 0];
  }, [standards, standardRef]);

  const [prevDrLink, nextDrLink] = useMemo(() => {
    return [
      getDisclosureRequirementLink({
        companyId: companyId,
        assessmentId: esrsAssessmentId,
        standardRef,
        disclosureRequirement: {
          reference: standard?.disclosureRequirementRefs[currentDrIndex - 1]?.reference ?? '',
          type: standard?.disclosureRequirementRefs[currentDrIndex - 1]?.type ?? '',
        },
        framework,
      }),
      getDisclosureRequirementLink({
        companyId: companyId,
        assessmentId: esrsAssessmentId,
        standardRef,
        disclosureRequirement: {
          reference: standard?.disclosureRequirementRefs[currentDrIndex + 1]?.reference ?? '',
          type: standard?.disclosureRequirementRefs[currentDrIndex + 1]?.type ?? '',
        },
        framework,
      }),
    ];
  }, [standard, currentDrIndex]);

  const [prevDr, nextDr] = useMemo(() => {
    const prevDiscloure = standard?.disclosureRequirementRefs[currentDrIndex - 1];
    const nextDisclosure = standard?.disclosureRequirementRefs[currentDrIndex + 1];

    return [
      prevDiscloure
        ? {
            title: prevDiscloure.title,
            reference: prevDiscloure.reference,
            label: 'Previous DR',
            link: prevDrLink,
          }
        : undefined,
      nextDisclosure
        ? {
            title: nextDisclosure.title,
            reference: nextDisclosure.reference,
            label: 'Next DR',
            link: nextDrLink,
          }
        : undefined,
    ];
  }, [standard, currentDrIndex]);

  const loading = isLoading || loadingCompany || loadingGroup;

  const hardcodedMetrics = useMemo(
    () => getHardcodedMetrics(disclosureRequirementRef),
    [disclosureRequirementRef]
  );

  const mdrmFilteredMetrics = useMemo(
    () =>
      answersData?.esrs_Answer
        .filter((m) => m.datapoints.find((dp) => dp.isMdrmDisclosed))
        .filter((m) => !m.hasOptedOut)
        .filter(
          (m) =>
            hardcodedMetrics.includes(m.metricRef) ||
            (disclosureRequirementRef === 'E1-6' ? m.metricRef === 'grossScopeGHG2' : '')
        ),
    [answersData, hardcodedMetrics, disclosureRequirementRef]
  );

  useEffect(() => {
    if (!rowData) setSelectedMetric(undefined);
  }, [rowData]);

  const isMFStandard = mfStandardRefs.includes(standardRef);

  return (
    <HStack>
      <VStack
        key={metricDR?.reference}
        overflowY="scroll"
        height="100%"
        flex={1}
        alignItems="stretch"
        w="100%"
        pt="16px"
        onScroll={handleScroll}
        paddingInline="8px"
        bg="bg.muted"
        css={{
          '&::-webkit-scrollbar': {
            width: '0',
          },
          scrollbarWidth: 'none',
        }}
        ref={reportRef}
      >
        <VStack alignItems="stretch" margin="auto" maxW="780px">
          <VStack
            minW="712px"
            w="780px"
            maxWidth="780px"
            p="40px 48px"
            margin="auto"
            shadow="interactive.accent"
            background="bg.elevated.interactive"
            alignItems="start"
            borderRadius="12px"
            gap="16px"
            minH=" 1200px"
          >
            {loading ? (
              <Loader />
            ) : (
              <>
                {showBackToTop && <BackToTopButton containerRef={reportRef} />}
                <VStack gap="16px" alignItems="start">
                  {showCategoryName && <Typography variant="h1">{categoryName}</Typography>}
                  {showStandardName && <Typography variant="h2">{standardName}</Typography>}
                  <Typography variant="h3">
                    {isMFStandard ? '' : metricDR?.reference} {metricDR?.title}
                  </Typography>
                </VStack>
                {isEsrs && !isTargetOrAction && (
                  <>
                    <DRHardcodedTables
                      disclosureRequirementRef={disclosureRequirementRef}
                      aggregatedMetrics={allAggregatedMetrics}
                      answersMap={answersMap}
                      year={year}
                      rowData={rowData}
                      selectedMetric={selectedMetric}
                      setSelectedMetric={setSelectedMetric}
                      setRowData={setRowData}
                      targets={targetsData}
                    />
                    {mdrmFilteredMetrics?.map((metric) => {
                      const aggregatedMetric = allAggregatedMetrics.find(
                        (m) => m.metric.reference === metric.metric.reference
                      );

                      return (
                        <StackedMdrmHardcodedTables
                          key={metric.metric.reference}
                          metric={aggregatedMetric?.metric}
                          aggregatedMetric={aggregatedMetric}
                          companyLevelReportingUnitId={companyLevelReportingUnitId}
                          setSelectedMetric={setSelectedMetric}
                          setRowData={setRowData}
                          rowData={rowData}
                          selectedMetric={selectedMetric}
                          selectedMdrm={selectedMdrm}
                          setSelectedMdrm={setSelectedMdrm}
                        />
                      );
                    })}
                  </>
                )}
                {!isTargetOrAction ? (
                  filteredMetrics?.map((m) => {
                    const metric = processedMetrics.find(
                      (met) => met.metric.reference === m.reference
                    );
                    const aggregatedMetric = filterOptedOutRows(
                      metric as AggregatedMetricsTableData,
                      companyAnswersData
                        .find((ans) => ans.isCompanyLevel)
                        ?.answers.filter((ans) => ans.hasOptedOut)
                        .map((ans) => ans.metricRef) ?? []
                    );
                    const hasOptedOut = answersData?.esrs_Answer.find(
                      (ans) => ans.metric.reference === m.reference
                    )?.hasOptedOut;
                    if (!hasOptedOut) {
                      if (m.metricType !== QuestionType_Enum_.Decimal_) {
                        return (
                          <NarrativeReportAnswer
                            metric={m}
                            answersMap={answersMap}
                            aggregatedMetrics={processedMetrics}
                            textAnswer={answersMap.get(m.reference)}
                            materialStandardId={companyAssessmentId}
                            companyLevelReportingUnitId={companyLevelReportingUnitId}
                            currency={company.company?.currency}
                            selectedMetric={selectedMetric}
                            setSelectedMetric={setSelectedMetric}
                            rowData={rowData}
                            setRowData={setRowData}
                          />
                        );
                      }
                      return (
                        m.metricType === QuestionType_Enum_.Decimal_ && (
                          <ClickableReportAnswer
                            metric={m}
                            aggregatedMetric={aggregatedMetric}
                            materialStandardId={companyAssessmentId}
                            companyLevelReportingUnitId={companyLevelReportingUnitId}
                            currency={company.company?.currency}
                            setSelectedMetric={setSelectedMetric}
                            setRowData={setRowData}
                            rowData={rowData}
                            selectedMetric={selectedMetric}
                            selectedMdrm={selectedMdrm}
                            setSelectedMdrm={setSelectedMdrm}
                          />
                        )
                      );
                    }
                  })
                ) : (
                  <VStack w="100%" alignItems="stretch" gap="24px">
                    {!!withAssociationMetrics.length && (
                      <VStack w="100%" alignItems="start">
                        {isEsrs && (
                          <Typography variant="h3">
                            Disclosures per standard if {drTypeName} are set
                          </Typography>
                        )}
                        {withAssociationMetrics?.map((m) => {
                          const aggregatedMetric = processedMetrics.find(
                            (met) => met.metric.reference === m.reference
                          );
                          if (
                            m.metricType !== QuestionType_Enum_.Decimal_ &&
                            !m.parentMetrics.length
                          ) {
                            return (
                              <NarrativeReportAnswer
                                metric={m}
                                answersMap={answersMap}
                                aggregatedMetrics={processedMetrics}
                                textAnswer={answersMap.get(m.reference)}
                                materialStandardId={companyAssessmentId}
                                companyLevelReportingUnitId={companyLevelReportingUnitId}
                                currency={company.company?.currency}
                                selectedMetric={selectedMetric}
                                setSelectedMetric={setSelectedMetric}
                                rowData={rowData}
                                setRowData={setRowData}
                              />
                            );
                          }
                          return (
                            m.metricType === QuestionType_Enum_.Decimal_ && (
                              <ClickableReportAnswer
                                metric={m}
                                aggregatedMetric={aggregatedMetric}
                                materialStandardId={companyAssessmentId}
                                companyLevelReportingUnitId={companyLevelReportingUnitId}
                                currency={company.company?.currency}
                                setSelectedMetric={setSelectedMetric}
                                setRowData={setRowData}
                                rowData={rowData}
                                selectedMetric={selectedMetric}
                                selectedMdrm={selectedMdrm}
                                setSelectedMdrm={setSelectedMdrm}
                              />
                            )
                          );
                        })}
                      </VStack>
                    )}
                    {!!withoutAssociationMetrics.length && (
                      <VStack w="100%" alignItems="start">
                        {isEsrs && (
                          <Typography variant="h3">
                            Disclosures per standard if {drTypeName} are not set
                          </Typography>
                        )}
                        {withoutAssociationMetrics?.map((m) => {
                          const aggregatedMetric = processedMetrics.find(
                            (met) => met.metric.reference === m.reference
                          );
                          if (
                            m.metricType !== QuestionType_Enum_.Decimal_ &&
                            !m.parentMetrics.length
                          ) {
                            return (
                              <NarrativeReportAnswer
                                metric={m}
                                answersMap={answersMap}
                                aggregatedMetrics={processedMetrics}
                                textAnswer={answersMap.get(m.reference)}
                                materialStandardId={companyAssessmentId}
                                companyLevelReportingUnitId={companyLevelReportingUnitId}
                                currency={company.company?.currency}
                                selectedMetric={selectedMetric}
                                setSelectedMetric={setSelectedMetric}
                                rowData={rowData}
                                setRowData={setRowData}
                              />
                            );
                          }
                          return (
                            m.metricType === QuestionType_Enum_.Decimal_ && (
                              <ClickableReportAnswer
                                metric={m}
                                aggregatedMetric={aggregatedMetric}
                                materialStandardId={companyAssessmentId}
                                companyLevelReportingUnitId={companyLevelReportingUnitId}
                                currency={company.company?.currency}
                                setSelectedMetric={setSelectedMetric}
                                setRowData={setRowData}
                                rowData={rowData}
                                selectedMetric={selectedMetric}
                                selectedMdrm={selectedMdrm}
                                setSelectedMdrm={setSelectedMdrm}
                              />
                            )
                          );
                        })}
                      </VStack>
                    )}
                  </VStack>
                )}
              </>
            )}
          </VStack>
          <BottomNavigation prevElement={prevDr} nextElement={nextDr} />
        </VStack>
      </VStack>
      {rowData && (
        <Box w="330px">
          <OptOutModal
            isOpen={isOpen}
            onClose={onClose}
            assessmentId={esrsAssessmentId}
            reportingUnitId={companyLevelReportingUnitId}
            metric={selectedMetric}
            isCompany={false}
          />
          <Box
            position="fixed"
            top={`${TOP_MENU_HEIGHT}`}
            right="0px"
            width="352px"
            height={`calc(100vh - ${TOP_MENU_HEIGHT})`}
            bg="bg.default"
            overflowY="auto"
          >
            <MetricSidebar
              row={rowData}
              companyCurrency={company.company?.currency ?? ''}
              companyReportingUnit={companyLevelReportingUnitId}
              isGroup={isGroup}
              metric={rowData.metric}
              materialStandardId={companyAssessmentId}
              selectedQuarter={TimePeriodsEnums.year}
              selectedMetric={sidebarMetric}
              setSelectedMetric={setSidebarMetric}
              onMenuOpen={onOpen}
              setRowData={setRowData}
              isOpenedFromAI
              generatedSummaryStatus={generatedSummaryStatus}
              setGeneratedSummaryStatus={setGeneratedSummaryStatus}
              frameworkName={
                Frameworks[rowData?.metric?.requirement?.frameworkRef as keyof typeof FrameworkRefs]
              }
            />
          </Box>
        </Box>
      )}
    </HStack>
  );
};
